import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  MyLocation,
  Search,
  Map as MapIcon,
  NavigateNext,
} from '@mui/icons-material';
import { FtthCaixa } from '../../services/ftthCaixaService';
import { MapaPontoCto } from '../../services/monitoramentoService';
import CtoMonitoramentoDetailsModal, { ftthCaixaToCtoSummary } from './CtoMonitoramentoDetailsModal';

export interface ViabilidadeResultado extends FtthCaixa {
  distancia: number;
  portas_livres: number;
}

interface ViabilidadeModalProps {
  open: boolean;
  onClose: () => void;
  caixas: FtthCaixa[];
  loadingCaixas?: boolean;
  onMostrarNoMapa: (caixasFiltradas: ViabilidadeResultado[]) => void;
  onPickOnMap?: () => void;
  initialLat?: string;
  initialLng?: string;
  portalContainer?: HTMLElement | null;
  showMapActions?: boolean;
}

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3;
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2)
    + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const getPortasLivres = (caixa: FtthCaixa) => {
  const total = caixa.capacidade || 0;
  const occupied = caixa.portas_em_uso || 0;
  const disabled = Array.isArray(caixa.disabled_ports) ? caixa.disabled_ports.length : 0;
  return Math.max(0, total - occupied - disabled);
};

const ViabilidadeModal: React.FC<ViabilidadeModalProps> = ({
  open,
  onClose,
  caixas,
  loadingCaixas = false,
  onMostrarNoMapa,
  onPickOnMap,
  initialLat,
  initialLng,
  portalContainer,
  showMapActions = true,
}) => {
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [distancia, setDistancia] = useState('200');
  const [resultados, setResultados] = useState<ViabilidadeResultado[]>([]);
  const [verificando, setVerificando] = useState(false);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [pesquisado, setPesquisado] = useState(false);
  const [detailsCaixaId, setDetailsCaixaId] = useState<number | null>(null);
  const [detailsCtoSummary, setDetailsCtoSummary] = useState<MapaPontoCto | null>(null);

  useEffect(() => {
    if (open) {
      if (initialLat) setLat(initialLat);
      if (initialLng) setLng(initialLng);
      if (initialLat || initialLng) {
        setPesquisado(false);
        setResultados([]);
      }
    }
  }, [open, initialLat, initialLng]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;

    setLoadingGPS(true);
    setPesquisado(false);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLat(position.coords.latitude.toFixed(7));
        setLng(position.coords.longitude.toFixed(7));
        setLoadingGPS(false);
        setResultados([]);
      },
      () => {
        setLoadingGPS(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleVerificar = () => {
    if (!lat || !lng || !distancia || loadingCaixas) return;

    setVerificando(true);
    setPesquisado(false);

    window.setTimeout(() => {
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lng);
      const raio = parseFloat(distancia);

      const found = caixas
        .filter(c => c.tipo === 'cto' && c.latitude && c.longitude)
        .map((caixa) => {
          const d = calculateDistance(
            latitude,
            longitude,
            Number(caixa.latitude),
            Number(caixa.longitude),
          );
          return { ...caixa, distancia: d, portas_livres: getPortasLivres(caixa) };
        })
        .filter(c => c.distancia <= raio && c.portas_livres > 0)
        .sort((a, b) => a.distancia - b.distancia);

      setResultados(found);
      setVerificando(false);
      setPesquisado(true);
    }, 300);
  };

  const handleShowOnMap = () => {
    onMostrarNoMapa(resultados);
    onClose();
  };

  const handleOpenCaixaDetails = (box: ViabilidadeResultado) => {
    setDetailsCaixaId(box.id);
    setDetailsCtoSummary(ftthCaixaToCtoSummary(box));
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      container={portalContainer}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">Consultar Viabilidade</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        {loadingCaixas ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4} gap={1.5}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">Carregando caixas...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Latitude"
                fullWidth
                value={lat}
                onChange={(e) => {
                  setLat(e.target.value);
                  setPesquisado(false);
                  setResultados([]);
                }}
                placeholder="-15.7797"
                size="small"
              />
              <TextField
                label="Longitude"
                fullWidth
                value={lng}
                onChange={(e) => {
                  setLng(e.target.value);
                  setPesquisado(false);
                  setResultados([]);
                }}
                placeholder="-47.9297"
                size="small"
              />
            </Box>

            <TextField
              label="Distância máxima (metros)"
              fullWidth
              type="number"
              value={distancia}
              onChange={(e) => {
                setDistancia(e.target.value);
                setPesquisado(false);
                setResultados([]);
              }}
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">m</InputAdornment>,
              }}
            />

            {showMapActions && onPickOnMap && (
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleUseMyLocation}
                  disabled={loadingGPS}
                  startIcon={loadingGPS ? <CircularProgress size={16} /> : <MyLocation />}
                  sx={{ whiteSpace: 'nowrap', py: 1 }}
                >
                  {loadingGPS ? 'Obtendo...' : 'Minha Posição'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={onPickOnMap}
                  startIcon={<MapIcon />}
                  sx={{ whiteSpace: 'nowrap', py: 1 }}
                >
                  Escolher no Mapa
                </Button>
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleVerificar}
              disabled={verificando || !lat || !lng || loadingCaixas}
              startIcon={verificando ? <CircularProgress size={20} color="inherit" /> : <Search />}
              sx={{ py: 1, borderRadius: 2 }}
            >
              {verificando ? 'Verificando...' : 'Verificar Disponibilidade'}
            </Button>

            {resultados.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                  {resultados.length} caixa(s) encontrada(s) no raio de {distancia}m:
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', borderRadius: 2 }}>
                  <List disablePadding>
                    {resultados.map((box, index) => (
                      <React.Fragment key={box.id}>
                        {index > 0 && <Divider />}
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => handleOpenCaixaDetails(box)} sx={{ py: 1.5 }}>
                            <ListItemText
                              primary={<Typography variant="body2" fontWeight="bold">{box.name}</Typography>}
                              secondary={(
                                <Box mt={0.5}>
                                  <Typography variant="caption" display="block">
                                    Distância: <strong>{Math.round(box.distancia)}m</strong>
                                  </Typography>
                                  <Typography variant="caption" color="success.main" fontWeight="bold">
                                    {box.portas_livres} portas disponíveis
                                  </Typography>
                                </Box>
                              )}
                            />
                            <NavigateNext color="action" />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {resultados.length === 0 && !verificando && pesquisado && (
              <Box py={3} textAlign="center" sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma caixa com portas disponíveis encontrada neste raio.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        {showMapActions && resultados.length > 0 && (
          <Button
            onClick={handleShowOnMap}
            variant="contained"
            color="primary"
            startIcon={<MapIcon />}
          >
            Mostrar no Mapa
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <CtoMonitoramentoDetailsModal
      open={detailsCaixaId !== null}
      caixaId={detailsCaixaId}
      initialCto={detailsCtoSummary}
      onClose={() => {
        setDetailsCaixaId(null);
        setDetailsCtoSummary(null);
      }}
      container={portalContainer}
    />
    </>
  );
};

export default ViabilidadeModal;
